import type { StatusRole } from '@lighthouse/design'

/**
 * THE ONE PRESENTATION of the outcome vocabulary the API serves.
 *
 * The server has one mapping from stored state to outcome — `ExecutionOutcomes.ForRun` and
 * `ExecutionOutcomes.ForEntity` (Lighthouse.Api Contracts/Models/ObservatoryExecutionModels.cs) —
 * and every surface that shows an outcome reads that vocabulary: Execution History (both grains),
 * the ETL Explorer, the Overview's Recent Executions and the Scheduler's run requests. Those pages
 * link to one another, so a row reached from one must read the same — same words, same colour — on
 * the next. Each surface used to keep its own map, and the same entity row was "Completed" on one
 * page and "Succeeded" on the page it linked to. Every map is now read from here.
 *
 * Pure and vitest-safe: it imports one type and nothing else.
 */

/** Every outcome either grain serves. */
export type ServedOutcome =
  | 'running'
  | 'stale'
  | 'succeeded'
  | 'succeededWithWarnings'
  | 'failed'
  | 'halted'
  | 'cancelled'
  | 'skipped'
  | 'pending'

/**
 * How an outcome is shown. A null role is a plain gray badge: StatusRole has no neutral member
 * (design-system types.ts), and a deliberate stop or a row nobody started is neither good news nor bad.
 */
export interface OutcomeLook {
  label: string
  role: StatusRole | null
}

export const OUTCOME_LOOK: Readonly<Record<ServedOutcome, OutcomeLook>> = {
  // 'good' is the role, 'Running' the label: StatusRole has no in-progress member, and work in
  // flight with a live heartbeat is not a problem.
  running: { label: 'Running', role: 'good' },
  // NEVER the running badge. A dead worker's leftover: nothing moves it out of Running, so it is
  // not in progress and may never end.
  stale: { label: 'Stale — no heartbeat', role: 'warning' },
  succeeded: { label: 'Succeeded', role: 'good' },
  succeededWithWarnings: { label: 'Succeeded with warnings', role: 'warning' },
  failed: { label: 'Failed', role: 'critical' },
  // A halt blocks later loads until an administrator clears it.
  halted: { label: 'Halted', role: 'critical' },
  // Somebody stopped it on purpose; not a fault, not a success. A run, or an entity stopped because its run was —
  // never counted as a failure (a worker shutting down under an entity is "failed", because its run resumes it).
  cancelled: { label: 'Cancelled', role: null },
  // The source object is absent: "not a failure, and not a warning either" (ObservatoryEtlRunsController),
  // so a plain badge — but the label says why, because a bare "Skipped" reads as a choice somebody made.
  skipped: { label: 'Skipped — source object absent', role: null },
  // Enrolled in its run, never started — behind a halt or a failure, for instance.
  pending: { label: 'Not started', role: null },
}

/** The outcomes a RUN can have (ExecutionOutcomes.RunOutcomes), in the status filter's order. */
export const RUN_OUTCOME_VOCABULARY = [
  'running', 'stale', 'succeeded', 'succeededWithWarnings', 'failed', 'halted', 'cancelled',
] as const satisfies readonly ServedOutcome[]

/**
 * The outcomes an ENTITY row can have (ExecutionOutcomes.EntityOutcomes). A row is never halted; it is cancelled when
 * its run was (EtlEntityRunState.Cancelled).
 */
export const ENTITY_OUTCOME_VOCABULARY = [
  'running', 'stale', 'succeeded', 'succeededWithWarnings', 'failed', 'skipped', 'pending', 'cancelled',
] as const satisfies readonly ServedOutcome[]

/**
 * A value this build does not know — the server serves a new enum member by its own name — shown
 * as itself on a plain badge: never mapped to a confident guess, and never green.
 */
export function unknownOutcomeLook(outcome: string): OutcomeLook {
  return { label: outcome.trim() || 'Unknown outcome', role: null }
}

function lookIn(vocabulary: readonly ServedOutcome[], outcome: string): OutcomeLook {
  return (vocabulary as readonly string[]).includes(outcome)
    ? OUTCOME_LOOK[outcome as ServedOutcome]
    : unknownOutcomeLook(outcome)
}

/** A run's outcome. An entity-only value (skipped, pending) is unknown here, as the server never sends one. */
export function runOutcomeLook(outcome: string): OutcomeLook {
  return lookIn(RUN_OUTCOME_VOCABULARY, outcome)
}

/** An entity row's outcome. The run-only value (halted) is unknown here, as the server never sends one. */
export function entityOutcomeLook(outcome: string): OutcomeLook {
  return lookIn(ENTITY_OUTCOME_VOCABULARY, outcome)
}
